/**
 * Single gate for whether analytics should actually fire.
 *
 * Returns false when:
 *   - the React build is not production (CRA dev server, jest, etc.), OR
 *   - the page is running on a localhost-like hostname even when built
 *     for production (i.e. `npm run build && serve` on the dev machine).
 *
 * The hostname check is what makes this robust against the
 * `build && serve locally to debug` workflow — NODE_ENV alone wouldn't
 * catch that. We also bail out completely when `window` is unavailable
 * (SSR / unit tests) so we never crash a non-browser context.
 *
 * Memoised — the answer is constant for the lifetime of the page, no
 * point recomputing on every event.
 */

let cached = null;

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

function compute() {
    if (typeof window === 'undefined') return false;
    if (process.env.NODE_ENV !== 'production') return false;
    const host = ((window.location && window.location.hostname) || '').toLowerCase();
    if (!host) return true;
    if (LOCAL_HOSTNAMES.has(host)) return false;
    if (host.endsWith('.local')) return false;
    return true;
}

export function isAnalyticsEnabled() {
    if (cached === null) cached = compute();
    return cached;
}

/**
 * Test-only: reset the memoised result so unit tests can flip the
 * environment and re-check. Never call from app code.
 */
export function _resetAnalyticsEnabledCacheForTests() {
    cached = null;
}
